import { useInfiniteQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { authHeader } from "@/utils/headers";

export interface Notification {
  id: number;
  message: string;
  user_id: number;
  workspace_id: number;
  created_at: string;
  dismissed?: boolean;
  title: string;
  link: string;
  metadata: Record<string, unknown>;
  priority: "low" | "medium" | "high";
  object_type: string;
  object_id: number;
  triggered_by_email?: string | null;
}

interface GetNotificationsParams {
  per_page?: number;
}

interface GetNotificationsResponse {
  success: boolean;
  notifications: Notification[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
  next_num: number | null;
  prev_num: number | null;
}

const useNotifications = (params?: GetNotificationsParams) => {
  const { workspaceId } = useParams();
  const auth = useAuth();

  return useInfiniteQuery<GetNotificationsResponse>({
    queryKey: ["notifications", workspaceId, params],
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = new URLSearchParams({
        page: String(pageParam),
        per_page: String(params?.per_page ?? 20),
      });

      const response = await fetch(
        `${ENDPOINTS.get_notifications(Number(workspaceId))}?${queryParams}`,
        {
          method: "GET",
          headers: authHeader(auth?.userToken ?? ""),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch notifications");
      }

      return response.json();
    },
    getNextPageParam: lastPage => (lastPage.has_next ? lastPage.next_num : undefined),
    getPreviousPageParam: firstPage => (firstPage.has_prev ? firstPage.prev_num : undefined),
    enabled: !!workspaceId,
    staleTime: 30000, // 30 seconds
  });
};

export default useNotifications;
